import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Icon } from "../icons";
import { useMe } from "../../hooks/useMe";
import { useWorkforceRoster } from "../../hooks/useWorkforce";
import { useAppStore } from "../../state/useAppStore";
import { roleLabel } from "../../lib/roleLabel";
import { usePersona } from "../../hooks/usePersona";
import { UserMenu } from "./UserMenu";
import { HelpDrawer } from "../help/HelpDrawer";
import { BrandMark } from "../BrandMark";

export function TopBar() {
  const { data: me } = useMe();
  const { workspaceId } = useParams<{ workspaceId: string }>();
  const persona = usePersona(workspaceId);
  // Live count of agents installed in this workspace. Driving the
  // top-right workforce badge so users see the inventory shift the
  // moment they smart-match an agent onto a step (which now triggers
  // an install via the assign endpoint).
  // The roster, not the PlayStudio install list. The old count was of
  // installed prompt-hands, a catalogue that is empty in workforce mode, so
  // the badge read 0 on a page whose whole subject is the workforce.
  const { data: roster } = useWorkforceRoster(workspaceId);
  // Count only enabled installs — the My Hands endpoint also returns
  // disabled installs (so users can re-enable them on that page), but
  // the fleet badge should reflect what's currently usable.
  // Optional all the way down: the TopBar renders on every page, so an
  // unexpected shape here would blank the entire application rather than one
  // badge.
  const fleetCount = roster?.hands?.length ?? null;
  const navigate = useNavigate();
  const location = useLocation();
  const setActive = useAppStore((s) => s.setActiveWorkspaceId);
  const activeId = useAppStore((s) => s.activeWorkspaceId);
  const [open, setOpen] = useState(false);
  const [helpOpen, setHelpOpen] = useState(false);
  const ref = useRef<HTMLDivElement | null>(null);

  // Sync the active workspace id from the URL so the store mirrors the URL.
  useEffect(() => {
    if (workspaceId && workspaceId !== activeId) setActive(workspaceId);
  }, [workspaceId, activeId, setActive]);

  // Close dropdown on outside click.
  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, [open]);

  const memberships = me?.memberships ?? [];
  const current = memberships.find((m) => m.workspaceId === (workspaceId ?? activeId));
  const initials = (me?.user.displayName ?? "?")
    .split(" ")
    .map((s) => s.charAt(0).toUpperCase())
    .join("")
    .slice(0, 2);

  const switchTo = (id: string) => {
    setActive(id);
    setOpen(false);
    // Preserve only the FIRST sub-segment across the switch (e.g.
    // /workspaces/<old>/dashboard → /workspaces/<new>/dashboard) but
    // strip deeper segments so we don't end up pointing at an entity
    // id that only exists in the prior workspace
    // (/plays/<old-id>, /tasks/<old-id>, /hands/builder/<old-id>).
    // /workspaces/<old>          → /workspaces/<new>
    // /workspaces/<old>/dashboard → /workspaces/<new>/dashboard
    // /workspaces/<old>/plays/X  → /workspaces/<new>/plays
    const match = location.pathname.match(
      /^\/workspaces\/[^/]+\/([^/]+)/,
    );
    const firstSegment = match?.[1];
    // Whitelist: only land on segments that are workspace-scoped index
    // pages (no required entity id). Anything else falls back to the
    // PersonaHome index, which is always safe.
    const SAFE_SEGMENTS = new Set([
      "dashboard", "plays", "operator", "agents",
      "outcomes", "people", "subscription", "settings",
    ]);
    if (firstSegment && firstSegment.startsWith("hands")) {
      // /hands/library, /hands/my, /hands/builder — all safe indexes.
      navigate(`/workspaces/${id}/hands/library`);
    } else if (firstSegment && SAFE_SEGMENTS.has(firstSegment)) {
      navigate(`/workspaces/${id}/${firstSegment}`);
    } else {
      navigate(`/workspaces/${id}`);
    }
  };

  return (
    <header className="topbar" data-testid="topbar">
      <BrandMark />
      <div className="brand-divider" />
      <div className="tenant" ref={ref} style={{ position: "relative" }}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label="Switch workspace"
          style={{
            background: "transparent",
            border: 0,
            display: "inline-flex",
            alignItems: "center",
            gap: 8,
            padding: 0,
            font: "inherit",
            color: "inherit",
            cursor: "pointer",
          }}
        >
          <span className="dot" />
          <span>{current?.workspaceName ?? "Select workspace"}</span>
          <span className="caret">▾</span>
        </button>
        {current ? (
          <span style={{ marginLeft: 8 }}>
            {/* Who you are here. The Xamun Intelligence domain filter that used to
                sit in this chip — People & Capability, Finance, Offer — filtered
                surfaces this product no longer has; nothing here read it. */}
            <span className="tenant" data-testid="role-chip" style={{ cursor: "default" }}>
              <span style={{ opacity: 0.7 }}>{roleLabel(persona ?? current.role)}</span>
            </span>
          </span>
        ) : null}
        {open ? (
          <ul
            role="listbox"
            aria-label="Workspaces"
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              left: 0,
              minWidth: 240,
              background: "var(--bg)",
              border: "1px solid var(--border)",
              borderRadius: 8,
              boxShadow: "var(--shadow)",
              padding: 4,
              listStyle: "none",
              margin: 0,
              zIndex: 50,
            }}
          >
            {memberships.length === 0 ? (
              <li style={{ padding: "8px 10px", fontSize: 12, color: "var(--text-3)" }}>
                No workspaces
              </li>
            ) : (
              memberships.map((m) => {
                const isActive = m.workspaceId === (workspaceId ?? activeId);
                return (
                  <li key={m.workspaceId}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isActive}
                      onClick={() => switchTo(m.workspaceId)}
                      style={{
                        width: "100%",
                        textAlign: "left",
                        background: isActive ? "var(--bg-soft)" : "transparent",
                        border: 0,
                        padding: "8px 10px",
                        borderRadius: 6,
                        cursor: "pointer",
                        font: "inherit",
                        display: "flex",
                        flexDirection: "column",
                        gap: 2,
                      }}
                    >
                      <span style={{ fontSize: 13, fontWeight: 500 }}>{m.workspaceName}</span>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>
                        {m.cxoRoleTag} · {roleLabel(m.role)}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        ) : null}
      </div>
      <div className="spacer" />
      <a
        className="tb-open-app"
        href={`/app/${workspaceId ?? current?.workspaceId ?? ""}/thread`}
        data-testid="open-app"
        title="Open the everyday app: one thread, your decisions, your reports"
      >
        Open the app
      </a>
      <div
        className="fid-badge"
        title={fleetCount === null ? "Loading workforce…" : `${fleetCount} agent${fleetCount === 1 ? "" : "s"} on the roster`}
        data-testid="fleet-badge"
        onClick={() => workspaceId && navigate(`/workspaces/${workspaceId}/workforce`)}
        style={{ cursor: workspaceId ? "pointer" : "default" }}
      >
        <span
          style={{
            width: 7,
            height: 7,
            borderRadius: "50%",
            background: "var(--accent)",
            display: "inline-block",
          }}
        />
        <span>Workforce</span>
        <span className="pct">{fleetCount === null ? "—" : fleetCount}</span>
      </div>
      <button
        type="button"
        className="icon-btn"
        aria-label="Help"
        data-testid="help-button"
        onClick={() => setHelpOpen(true)}
        title="Help & Documentation (Esc to close)"
      >
        <Icon name="help" className="ic-svg sm" />
      </button>
      <UserMenu
        initials={initials || "?"}
        displayName={me?.user.displayName ?? ""}
        email={me?.user.email ?? ""}
      />
      <HelpDrawer isOpen={helpOpen} onClose={() => setHelpOpen(false)} />
    </header>
  );
}
